import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Download, ArrowRight } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';

/**
 * Página de confirmación de registro exitoso
 */
const RegistroConfirmacion = () => {
  const location = useLocation();
  const datosEstudiante = location.state?.datosEstudiante;

  if (!datosEstudiante) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <p className="text-gray-600 mb-4">
            No se encontraron datos del registro. Por favor intente nuevamente.
          </p>
          <Link to="/registro">
            <Button variant="primary" className="w-full">
              Volver al Registro
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Encabezado */}
        <div className="text-center mb-8">
          <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            ¡Registro Completado!
          </h1>
          <p className="text-gray-600">
            El estudiante ha sido registrado exitosamente en el colegio
          </p>
        </div>

        {/* Información del registro */}
        <Card className="mb-8">
          <div className="space-y-6">
            {/* Número de registro */}
            <div className="text-center p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
              <p className="text-sm text-gray-600 uppercase mb-2">Número de Registro</p>
              <p className="text-3xl font-mono font-bold text-green-700">
                {datosEstudiante.registro_estudiante}
              </p>
              <p className="text-xs text-gray-600 mt-3">
                Guarde este número para futuras referencias
              </p>
            </div>

            {/* Datos del estudiante */}
            <div className="border-2 border-gray-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Datos del Estudiante</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Nombre Completo</p>
                  <p className="font-medium text-gray-900">
                    {datosEstudiante.nombre} {datosEstudiante.apellido}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Cédula</p>
                  <p className="font-medium text-gray-900">{datosEstudiante.ci}</p>
                </div>
                {datosEstudiante.paralelo && (
                  <>
                    <div>
                      <p className="text-gray-600">Curso</p>
                      <p className="font-medium text-gray-900">
                        {datosEstudiante.paralelo.curso}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Paralelo</p>
                      <p className="font-medium text-gray-900">
                        {datosEstudiante.paralelo.nombre}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Próximos pasos */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ArrowRight size={20} className="text-blue-600" />
                Próximos Pasos
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex gap-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-medium">
                    1
                  </span>
                  <span>Se enviará un correo de confirmación al tutor registrado</span>
                </li>
                <li className="flex gap-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-medium">
                    2
                  </span>
                  <span>Adjunte los documentos requeridos (certificado de nacimiento, cédula)</span>
                </li>
                <li className="flex gap-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-medium">
                    3
                  </span>
                  <span>Espere la confirmación final del colegio</span>
                </li>
              </ul>
            </div>

            {/* Información importante */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">
                ⚠️ Información Importante
              </h3>
              <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>El registro está pendiente de confirmación</li>
                <li>Debe proporcionar todos los documentos requeridos</li>
                <li>Recibirá notificaciones por correo durante el proceso</li>
                <li>Guarde el número de registro para consultas futuras</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Botones de acción */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Button
            onClick={() => window.print()}
            variant="secondary"
            className="flex items-center justify-center gap-2"
          >
            <Download size={18} /> Descargar Recibo
          </Button>
          <Link to="/" className="flex-1 md:flex-none">
            <Button variant="primary" className="w-full">
              Ir al Inicio
            </Button>
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-600">
          <p>
            ¿Tiene preguntas? Contáctenos a través de nuestro{' '}
            <a href="mailto:contacto@colegio.edu" className="text-blue-600 hover:underline">
              correo electrónico
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistroConfirmacion;
